import React from 'react';
import { resolveImageUrl } from '../services/apiService';

// ── US State abbreviation → full name map ─────────────────────────────────────
const US_STATES = {
  AL:'Alabama', AK:'Alaska', AZ:'Arizona', AR:'Arkansas', CA:'California',
  CO:'Colorado', CT:'Connecticut', DE:'Delaware', FL:'Florida', GA:'Georgia',
  HI:'Hawaii', ID:'Idaho', IL:'Illinois', IN:'Indiana', IA:'Iowa',
  KS:'Kansas', KY:'Kentucky', LA:'Louisiana', ME:'Maine', MD:'Maryland',
  MA:'Massachusetts', MI:'Michigan', MN:'Minnesota', MS:'Mississippi', MO:'Missouri',
  MT:'Montana', NE:'Nebraska', NV:'Nevada', NH:'New Hampshire', NJ:'New Jersey',
  NM:'New Mexico', NY:'New York', NC:'North Carolina', ND:'North Dakota', OH:'Ohio',
  OK:'Oklahoma', OR:'Oregon', PA:'Pennsylvania', RI:'Rhode Island', SC:'South Carolina',
  SD:'South Dakota', TN:'Tennessee', TX:'Texas', UT:'Utah', VT:'Vermont',
  VA:'Virginia', WA:'Washington', WV:'West Virginia', WI:'Wisconsin', WY:'Wyoming',
  DC:'Washington DC',
};

/** Returns full state name if code is a 2-letter abbreviation, otherwise returns as-is */
const expandState = (s) => {
  if (!s) return '';
  const upper = s.trim().toUpperCase();
  return US_STATES[upper] || s;
};

// ── Utility: US phone formatter ───────────────────────────────────────────────
export const formatUSPhone = (raw) => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (d.length !== 10) return raw; // return as-is if not 10 digits
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

// ── Utility: Haversine distance (miles) between two lat/lng points ─────────
const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const LoadingSpinner = ({ size = 'medium', text = 'Loading...' }) => (
  <div className={`loading-spinner-container ${size}`}>
    <div className="spinner" />
    {text && <p className="spinner-text">{text}</p>}
  </div>
);

export const StarRating = ({ rating = 0, size = 16, onChange }) => {
  return (
    <div className="star-rating" style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          style={{
            fontSize: size,
            cursor: onChange ? 'pointer' : 'default',
            color: star <= Math.round(rating) ? '#FF6B35' : '#ddd',
          }}
          onClick={() => onChange && onChange(star)}
        >
          ★
        </span>
      ))}
    </div>
  );
};

export const Badge = ({ text, type = 'default' }) => (
  <span className={`badge badge-${type}`}>{text}</span>
);

export const EmptyState = ({ icon = '🍽️', title, subtitle, action }) => (
  <div className="empty-state">
    <div className="empty-icon">{icon}</div>
    <h3 className="empty-title">{title}</h3>
    {subtitle && <p className="empty-subtitle">{subtitle}</p>}
    {action && (
      <button className="btn-primary" onClick={action.onClick}>
        {action.label}
      </button>
    )}
  </div>
);

// ── Default biryani / Indian-food images – one per slot ──────────────────────
const DEFAULT_FOOD_IMAGES = [
  'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=600&h=300&fit=crop', // biryani 1
  'https://images.unsplash.com/photo-1589302168068-964664d93dc0?w=600&h=300&fit=crop', // indian food
  'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=600&h=300&fit=crop', // biryani 2
  'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=600&h=300&fit=crop', // kebab
  'https://images.unsplash.com/photo-1606491956391-6a65cc16f34e?w=600&h=300&fit=crop', // curry
  'https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=600&h=300&fit=crop', // biryani 3
  'https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=600&h=300&fit=crop', // chicken curry
  'https://images.unsplash.com/photo-1574653853027-5382a3d23a15?w=600&h=300&fit=crop', // rice bowl
];

export const KitchenCard = ({ kitchen, onClick, onFavorite, isFavorite, index = 0 }) => {
  const [imgError, setImgError] = React.useState(false);
  
  const name = kitchen.kitchen_name || kitchen.name || 'Kitchen';
  const rating = kitchen.reviews?.average_rating || kitchen.average_rating || kitchen.rating || 0;
  const reviewCount = kitchen.reviews?.total_reviews || kitchen.review_count || kitchen.total_reviews || 0;
  const isOpen = kitchen.is_available !== false && kitchen.status !== 'closed';
  const deliveryTime = kitchen.delivery_time || kitchen.estimated_delivery_time || '30–40 min';

  // Cuisine tags
  const getCuisineTags = () => {
    const raw = kitchen.cuisine_type || kitchen.cuisine || '';
    if (!raw) return [];
    return raw.split(/[,/]/).map(t => t.trim()).filter(Boolean).slice(0, 3);
  };
  const cuisineTags = getCuisineTags();

  const subtitle = kitchen.description || (cuisineTags.length ? cuisineTags.join(', ') : '');

  // Pick a unique default image based on index
  const defaultImage = DEFAULT_FOOD_IMAGES[index % DEFAULT_FOOD_IMAGES.length];
  
  // Try multiple possible image fields, validate they're valid URLs
  const getValidImageUrl = () => {
    const possibleUrls = [
      kitchen.cover_image_url,
      kitchen.image_url,
      kitchen.profile_image_url,
      kitchen.profile_image,
      kitchen.logo_url
    ];
    
    for (const url of possibleUrls) {
      if (url && typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))) {
        return url;
      }
    }
    return null;
  };
  
  const kitchenImage = getValidImageUrl();
  const imageUrl = imgError || !kitchenImage ? defaultImage : kitchenImage;

  return (
    <div className="kitchen-card" onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* Hero Image */}
      <div className="kitchen-card-image">
        <img
          src={imageUrl}
          alt={name}
          className="kitchen-img"
          onError={(e) => { 
            if (!imgError) {
              setImgError(true);
              e.target.src = defaultImage;
            }
          }}
        />

        {/* Favorite btn */}
        {onFavorite && (
          <button
            className={`favorite-btn ${isFavorite ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); onFavorite(); }}
          >
            {isFavorite ? '❤️' : '🤍'}
          </button>
        )}

        {/* Bottom overlays: rating (left) + time (right) */}
        <div className="kitchen-card-bottom-row">
          {rating > 0 && (
            <div className="kc-rating-badge">
              <span className="kc-star">★</span>
              <span>{Number(rating).toFixed(1)}</span>
            </div>
          )}
          <div className="kc-time-badge">
            <span className="kc-clock">⏱</span>
            <span>{deliveryTime}</span>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="kitchen-card-info">
        {/* Name row + Open status */}
        <div className="kitchen-card-name-row">
          <h3 className="kitchen-name">{name}</h3>
          <span className={`kc-open-badge ${isOpen ? 'open' : 'closed'}`}>
            <span className="kc-open-dot" />
            {isOpen ? 'Open' : 'Closed'}
          </span>
        </div>

        {subtitle && <p className="kitchen-cuisine">{subtitle}</p>}

        {/* Cuisine tag chips */}
        {cuisineTags.length > 0 && (
          <div className="kitchen-tag-row">
            {cuisineTags.map(tag => (
              <span key={tag} className="kitchen-tag">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Horizontal Kitchen Row Card ──────────────────────────────────────────────
export const KitchenRowCard = ({ kitchen, onClick, onFavorite, isFavorite, index = 0, selectedLocation }) => {
  const [imgError, setImgError] = React.useState(false);

  const name = kitchen.kitchen_name || kitchen.name || 'Kitchen';
  const isOpen = kitchen.is_available !== false && kitchen.status !== 'closed';

  const getFullAddress = () =>
    [kitchen.address_street, kitchen.address_city, expandState(kitchen.address_state), kitchen.address_zip]
      .filter(Boolean).join(', ');

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const suffix = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${suffix}`;
  };

  const openTime = formatTime(kitchen.opening_time || kitchen.open_time);
  const closeTime = formatTime(kitchen.closing_time || kitchen.close_time);
  const timing = openTime && closeTime ? `${openTime} – ${closeTime}` : (openTime || '');

  const distance = (() => {
    // Prefer API-provided distance
    if (kitchen.distance_miles != null) return `${parseFloat(kitchen.distance_miles).toFixed(1)} mi away`;
    if (kitchen.distance != null) return `${parseFloat(kitchen.distance).toFixed(1)} mi away`;
    // Calculate from selected location coords → kitchen coords
    const sLat = selectedLocation?.latitude;
    const sLng = selectedLocation?.longitude;
    const kLat = kitchen.coordinates_lat;
    const kLng = kitchen.coordinates_lng;
    if (sLat && sLng && kLat && kLng) {
      const d = haversineDistance(parseFloat(sLat), parseFloat(sLng), parseFloat(kLat), parseFloat(kLng));
      return `${d.toFixed(1)} mi away`;
    }
    return '';
  })();

  const phone = kitchen.contact_phone || kitchen.phone || '';
  const formattedPhone = formatUSPhone(phone);

  const defaultImage = DEFAULT_FOOD_IMAGES[index % DEFAULT_FOOD_IMAGES.length];
  const getValidImageUrl = () => {
    // Prefer profile/logo images; resolveImageUrl handles relative API paths
    const candidates = [
      kitchen.profile_image_url,
      kitchen.profile_image,
      kitchen.logo_url,
      kitchen.cover_image_url,
      kitchen.image_url,
    ];
    for (const url of candidates) {
      if (url && typeof url === 'string' && url.trim()) {
        return resolveImageUrl(url);
      }
    }
    return null;
  };
  const imageUrl = imgError || !getValidImageUrl() ? defaultImage : getValidImageUrl();

  return (
    <div className="kitchen-row-card" onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* Image */}
      <div className="krc-img-wrap">
        <img src={imageUrl} alt={name} className="krc-img" onError={() => setImgError(true)} />
      </div>

      {/* 3-line body */}
      <div className="krc-body">
        {/* Line 1: Name (left) | Open/Closed (right) */}
        <div className="krc-line">
          <span className="krc-line-left krc-name">{name}</span>
          <span className={`krc-line-right krc-status ${isOpen ? 'open' : 'closed'}`}>
            <span className="krc-status-dot" />{isOpen ? 'Open' : 'Closed'}
          </span>
        </div>

        {/* Line 2: Address (left) | Timing (right) */}
        <div className="krc-line">
          <span className="krc-line-left krc-address">{getFullAddress() || '—'}</span>
          {timing && <span className="krc-line-right krc-timing">{timing}</span>}
        </div>

        {/* Line 3: Phone (left) | Distance (right) */}
        <div className="krc-line">
          <span className="krc-line-left krc-phone">{formattedPhone || '—'}</span>
          {distance && <span className="krc-line-right krc-distance">{distance}</span>}
        </div>
      </div>

      {/* Favourite button */}
      {onFavorite && (
        <button
          className={`krc-fav-btn ${isFavorite ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); onFavorite(); }}
        >
          {isFavorite ? '❤️' : '🤍'}
        </button>
      )}
    </div>
  );
};

export const Modal = ({ isOpen, onClose, title, children, size = 'medium' }) => {  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-container modal-${size}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

export const OTPInput = ({ length = 6, value, onChange }) => {
  const digits = value.split('');
  while (digits.length < length) digits.push('');

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 1);
    const newDigits = [...digits];
    newDigits[idx] = val;
    onChange(newDigits.join(''));
    if (val && idx < length - 1) {
      document.getElementById(`otp-${idx + 1}`)?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      document.getElementById(`otp-${idx - 1}`)?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
    e.preventDefault();
  };

  return (
    <div className="otp-input-container">
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          id={`otp-${idx}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[idx] || ''}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          onPaste={handlePaste}
          className="otp-box"
        />
      ))}
    </div>
  );
};

export const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = 'Confirm', dangerMode = false }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p>{message}</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
            <button className="btn-outline" onClick={onClose}>Cancel</button>
            <button className={dangerMode ? 'btn-danger' : 'btn-primary'} onClick={onConfirm}>
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

